import _ from "lodash";
import api from "CommonCore/api";

import {
  buildSubSchema,
  setupLookups,
  emptyOrFalse,
} from "App/shk-form-tab";
import moment from "moment";
import {
  ASTMODUSKEY,
  FINANZAMT,
  getModusFromAntrag,
} from "App/constants";

const KSA_DEUTSCHLAND = "a2c0483a-c66e-4d74-817e-e743d37d80b4";

const checkKeinWerkvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : getModusFromAntrag(antrag)) !== ASTMODUSKEY.WerkPrivat,
};

const checkWerkvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : getModusFromAntrag(antrag)) === ASTMODUSKEY.WerkPrivat,
};

const checkKeinLehrvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : getModusFromAntrag(antrag)) !== ASTMODUSKEY.Lehrauftrag,
};

const checkKeineHauptTaetig = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => entity => (modus !== undefined ? modus : getModusFromAntrag(antrag)) !== ASTMODUSKEY.Lehrauftrag || (!entity.p_ist_haupttaetigkeit && !entity.ap_ist_haupttaetigkeit),
};

const checkLehrvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : getModusFromAntrag(antrag)) === ASTMODUSKEY.Lehrauftrag,
};

const checkKeinVortrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : getModusFromAntrag(antrag)) !== ASTMODUSKEY.Vortrag,
};

const checkVortrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : getModusFromAntrag(antrag)) === ASTMODUSKEY.Vortrag,
};

// Kombiniert mehrere hideFromDetail/hideFromEditor-Checks per ODER (alle Checks müssen dasselbe inject-Signatur haben)
const orChecks = (...checks) => ({
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => entity => checks.some(check => check.generator(modus, antrag)(entity)),
});

const checkKeinLehrvertragUndKeinVortrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => {
    const mod = (modus !== undefined ? modus : getModusFromAntrag(antrag));
    return mod !== ASTMODUSKEY.Lehrauftrag && mod !== ASTMODUSKEY.Vortrag;
  },
};

const checkWerkvertragOderLehrvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => {
    const mod = (modus !== undefined ? modus : getModusFromAntrag(antrag));
    return mod === ASTMODUSKEY.Lehrauftrag || mod === ASTMODUSKEY.WerkPrivat;
  },
};

const checkInlandOderWerkOderTarif = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => entity => {
    const mod = (modus !== undefined ? modus : getModusFromAntrag(antrag));
    const istInland = (!entity.p_angehoerig_ksaid || entity.p_angehoerig_ksaid == KSA_DEUTSCHLAND) &&
                    (!entity.p_angehoerig2_ksaid || entity.p_angehoerig2_ksaid == KSA_DEUTSCHLAND);
    return istInland || mod === ASTMODUSKEY.Tarif || mod === ASTMODUSKEY.WerkPrivat;
  },
};

const checkInlandOderKeinTarif = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => entity => {
    const keinTarif = (modus !== undefined ? modus : getModusFromAntrag(antrag)) !== ASTMODUSKEY.Tarif;
    const istInland = (!entity.p_angehoerig_ksaid || entity.p_angehoerig_ksaid == KSA_DEUTSCHLAND) &&
                    (!entity.p_angehoerig2_ksaid || entity.p_angehoerig2_ksaid == KSA_DEUTSCHLAND);
    return keinTarif || istInland;
  },
};

const checkKeinSteuerId = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => entity => {
    const MODUS = modus || getModusFromAntrag(antrag);
    const relevanterModus = MODUS === ASTMODUSKEY.Lehrauftrag || MODUS === ASTMODUSKEY.Vortrag;
    return !relevanterModus || (entity.ap_land_ksaid || entity.p_land_ksaid) !== KSA_DEUTSCHLAND || (entity.p_ohne_verguetung || entity.ap_ohne_verguetung);
  },
};

const booleans = [{ value: true, label: "Ja" }, { value: false, label: "Nein" }];
const janein = value => (value === null || value === undefined) ? "" : value ? "Ja" : "Nein";


const getStaatsangField = (fieldname, label, required) => {
  return {
    field: fieldname,
    display: label,
    required: required,
    subtag: "stamm",
    hideFromDetail: checkWerkvertrag,
    hideFromEditor: checkWerkvertrag,
    type: "select",
    inject: "k_staatLookup",
    noSort: true,
    choices: lookup => lookup && _.map(lookup, staat => ({ value: staat.ksa_id, label: staat.ksa_adjektiv }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    selector: lookup => id => lookup && id && lookup[id].ksa_adjektiv,
  };
};

export const schema = {
  displayName: "Person",
  formatWithDefinitiveArticle: p => `die Person ${ p.p_vorname } ${ p.p_name }`,
  toUserString: ba => `Budgetart ${ ba.ba_kbez }`,
  defaultSort: [{ field: "p_name" }, { field: "p_vorname" }],
  table: [

    {
      field: "p_svakey",
      display: "Personalnummer",
      subtag: "stamm",
      required: true,
      readonly: true,
      hideFromDetail: checkVortrag,
      hideFromEditor: checkVortrag,
    },
    {
      field: "p_sva_lbvnr",
      display: "LBV-Personalnummer",
      subtag: "stamm",
      hideFromDetail: orChecks(checkLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkLehrvertrag, checkVortrag),
    },

    {
      field: "p_kanid",
      display: "Anrede",
      lookup: "k_anrede.select.kan_id.kan_kbez",
      subtag: "stamm",
      required: true,
    },
    {
      field: "p_karid",
      display: "Titel",
      lookup: "k_anredetitel.select.kar_id.kar_bez",
      subtag: "stamm",
      required: false,
      hideFromDetail: checkKeinLehrvertragUndKeinVortrag,
      hideFromEditor: checkKeinLehrvertragUndKeinVortrag,
    },

    {
      field: "p_vorname",
      display: "Vorname",
      helpText: "Geben Sie Ihren Vornamen gemäß amtlichem Ausweisdokument an, max. 30 Zeichen.",
      maxlength: 30,
      subtag: "stamm",
      required: true,
      inject: "user",
      initValue: user => () => {
        return user && user.vorname;
      },
    },
    {
      field: "p_name",
      display: "Name",
      helpText: "Geben Sie Ihren Nachnamen an, max. 35 Zeichen.",
      maxlength: 35,
      subtag: "stamm",
      required: true,
      inject: "user",
      initValue: user => () => {
        return user && user.name;
      },

    },
    {
      field: "p_gebname",
      display: "Geburtsname",
      maxlength: 28,
      hideFromDetail: orChecks(checkLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkLehrvertrag, checkVortrag),
      subtag: "stamm",
      // PBP-3507 required: (entity) => entity.p_kfaid == '5f53617e-8f2f-4656-9454-442fee38ab46' //verheiratet
    },
    {
      field: "p_gebdatum",
      display: "Geburtsdatum",
      type: "date",
      subtag: "stamm",
      required: true,
      validate: value => {
        if (value) {
          const year = moment(value).year();
          if (year < 1930 || moment(value) > moment()) {
            return "Das Geburtsdatum muss zwischen 1930 und heute liegen";
          }
        }
      },
    },
    {
      field: "p_gebort",
      display: "Geburtsort",
      maxlength: 35,
      subtag: "stamm",
      required: true,
      hideFromDetail: orChecks(checkWerkvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertrag, checkVortrag),
    },

    {
      field: "p_geb_ksaid",
      display: "Geburtsland",
      required: true,
      lookup: "k_staat.select.ksa_id.ksa_kbez",
      subtag: "stamm",
      hideFromDetail: orChecks(checkWerkvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertrag, checkVortrag),

    },
    getStaatsangField("p_angehoerig_ksaid", "Staatsangehörigkeit", true),
    _.assign(getStaatsangField("p_angehoerig2_ksaid", "2. Staatsangehörigkeit", false), {
      hideFromDetail: orChecks(checkWerkvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertrag, checkVortrag),
    }),
    {
      field: "info_ausland",
      type: "plaintext",
      display: "Wenn es sich bei ihrer Staatsangehörigkeit nicht um ein EU-Mitgliedsland handelt, so reichen Sie bitte bereits mit dem Antrag eine elektronische Kopie Ihres aktuellen Aufenthaltstitels mit ein. Später beim Einreichen des Dienstvertrags ist zusätzlich eine Kopie in Papierform beizufügen.",
      useHeader: false,
      subtag: "stamm",
      hideFromDetail: orChecks(checkInlandOderWerkOderTarif, checkVortrag),
      hideFromEditor: orChecks(checkInlandOderWerkOderTarif, checkVortrag),

    },
    {
      field: "info_ausland",
      type: "plaintext",
      display: "Wenn es sich bei ihrer Staatsangehörigkeit nicht um ein EU-Mitgliedsland handelt, so reichen Sie bitte bereits mit dem Antrag eine elektronische Kopie Ihres aktuellen Aufenthaltstitels mit ein. Später beim Einreichen des Arbeitsvertrags ist zusätzlich eine Kopie in Papierform beizufügen.",
      useHeader: false,
      subtag: "stamm",
      hideFromDetail: orChecks(checkInlandOderKeinTarif, checkVortrag),
      hideFromEditor: orChecks(checkInlandOderKeinTarif, checkVortrag),

    },
    {
      field: "p_ist_haupttaetigkeit",
      display: "Hauptberuflich tätig",
      subtag: "stamm",
      required: true,
      type: "radio",
      selector: janein,
      choices: booleans,
      hideFromDetail: checkKeinLehrvertrag,
      hideFromEditor: checkKeinLehrvertrag,
    },
    {
      field: "p_haupttaetigkeit",
      display: "Hauptberufliche Tätigkeit",
      subtag: "stamm",
      required: true,
      hideFromDetail: checkKeineHauptTaetig,
      hideFromEditor: checkKeineHauptTaetig,
    },
    {
      field: "p_dienstort",
      display: "Dienstort",
      subtag: "stamm",
      required: true,
      hideFromDetail: checkKeineHauptTaetig,
      hideFromEditor: checkKeineHauptTaetig,

    },
    {
      field: "p_lehr_zustimmung",
      display: "Ich erkläre mich mit der Übergangsregelung des § 127 SGB IV einverstanden und stimme zu, dass ich den Lehrauftrag als Selbstständige*r wahrnehmen werde",
      initValue: false,
      hideFromDetail: checkKeinLehrvertrag,
      hideFromEditor: checkKeinLehrvertrag,
      lookup: "boolean.checkbox",
      subtag: "stamm",
    },

    // (-- Vortrag/Kolloquium
    {
      field: "p_selbstaendig",
      display: "Selbständig tätig",
      subtag: "stamm",
      type: "radio",
      selector: janein,
      choices: booleans,
      required: true,
      hideFromDetail: checkKeinVortrag,
      hideFromEditor: checkKeinVortrag,
      dependends: [
        {
          fields: ["info_selbstaendig_ja"],
          invisibleIf: value => value !== true,
          noContainer: true,
        },
        {
          fields: ["info_selbstaendig_nein"],
          invisibleIf: value => value !== false,
          noContainer: true,
        },
      ],
    },
    {
      field: "info_selbstaendig_ja",
      type: "plaintext",
      display: "Falls Sie selbständig sind oder im Auftrag einer Firma/eines Büros handeln, wenden Sie sich bitte im Vorfeld an das Dezernat 1.3 (Beschaffungsantrag).",
      useHeader: false,
      subtag: "stamm",
      hideFromDetail: checkKeinVortrag,
      hideFromEditor: checkKeinVortrag,
    },
    {
      field: "info_selbstaendig_nein",
      type: "plaintext",
      display: "Ich bin im Rahmen dieses Vortrags/Kolloquiums nicht als Unternehmer*in im Sinne des Umsatzsteuerrechts anzusehen (Ich übe keine gewerbliche oder berufliche Tätigkeit selbständig aus, die auf eine dauerhafte Erzielung von Einnahmen angelegt ist, § 2 Abs. 1 UStG).",
      useHeader: false,
      subtag: "stamm",
      hideFromDetail: checkKeinVortrag,
      hideFromEditor: checkKeinVortrag,
    },
    {
      field: "p_honorar_zustimmung",
      display: "Ich bestätige, dass mir bekannt ist, dass mit der Auszahlung des Honorarbetrages (Brutto) alle Kosten abgegolten sind und dass ich für eine evtl. Versteuerung des Honorars selbst verantwortlich bin.",
      type: "checkbox",
      initValue: false,
      required: true,
      subtag: "stamm",
      hideFromDetail: checkKeinVortrag,
      hideFromEditor: checkKeinVortrag,
      validate: value => {
        if (!value) {
          return "Bitte bestätigen Sie diese Angabe.";
        }
      },
    },
    // )-- Vortrag/Kolloquium

    // (-- Werkvertrag
    {
      field: "p_eubuerger",
      display: "EU-Bürger",
      type: "checkbox",
      initValue: () => true,
      hideFromDetail: checkKeinWerkvertrag,
      hideFromEditor: checkKeinWerkvertrag,
      required: true,
      subtag: "stamm",
      dependends: [
        {
          fields: ["info_ausland_werkvertrag"],
          invisibleIf: value => value, // nur anzeigen wenn kein EU-Bürger
          noContainer: true,
        },
      ],
    },
    {
      field: "info_ausland_werkvertrag",
      type: "plaintext",
      display: "Wenn es sich bei ihrer Staatsangehörigkeit nicht um ein EU-Mitgliedsland handelt, so reichen Sie bitte bereits mit dem Antrag eine elektronische Kopie Ihres aktuellen Aufenthaltstitels mit ein. Später beim Einreichen des Dienstvertrags ist zusätzlich eine Kopie in Papierform beizufügen.",
      useHeader: false,
      subtag: "stamm",
      hideFromDetail: checkKeinWerkvertrag,
      hideFromEditor: checkKeinWerkvertrag,
    },
    {
      field: "p_finanzamt",
      display: "Zuständiges Finanzamt",
      hideFromDetail: checkKeinWerkvertrag,
      hideFromEditor: checkKeinWerkvertrag,
      maxlength: 200,
      required: true,
      subtag: "stamm",
    },
    // )-- Werkvertrag

    // (-- Kein Lehrvertrag
    {
      field: "p_kfaid",
      display: "Familienstand",
      required: true,
      type: "select",
      choices: lookup => lookup && _.map(_.filter(lookup, l => l.kfa_anzeige), l => ({ value: l.kfa_id, label: l.kfa_kbez })),
      inject: ["k_familienstandLookup"],
      selector: lookup => id => id && lookup && lookup[id].kfa_kbez,
      hideFromDetail: orChecks(checkLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkLehrvertrag, checkVortrag),
      subtag: "stamm",
      dependends: [
        {
          fields: ["p_famstandseit"],
          invisibleIf: value => !value || value == "32a05505-cb18-429c-826d-f54c52789893" || value == "20057dc0-d3ff-4367-a5d2-dc2770748cfd", // 1. ID auf guarda korrekt, 2. auf buw-test?
          noContainer: true,
        },
      ],
    },
    {
      field: "p_famstandseit",
      display: "Familienstand seit",
      type: "date",
      hideFromDetail: orChecks(checkLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkLehrvertrag, checkVortrag),
      subtag: "stamm",
    },
    {
      field: "info_schwerbehindert",
      display: "Die folgende Angabe ist freiwillig und dient lediglich dem Zweck der erleichterten und rechtzeitigen Einbindung der gesetzlich zuständigen Interessensvertretung.",
      type: "plaintext",
      hideFromDetail: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      subtag: "stamm",
      useHeader: false,
    },
    {
      field: "p_schwerbehindert_jn",
      display: "Liegt bei Ihnen ein festgestellter Grad der Behinderung (GdB) vor?",
      lookup: {
        editType: "radioheader2",
        values: [{ value: false, label: "Nein bzw. keine Angabe" },
                 { value: true, label: "Ja" }],
      },
      dependends: [
        {
          fields: ["p_behindertengrad", "p_bescheidvon"],
          invisibleIf: emptyOrFalse,
        },
      ],
      hideFromDetail: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      subtag: "stamm",
    },
    {
      field: "p_behindertengrad",
      display: "Grad der Behinderung",
      type: "number",
      required: entity => !!entity.p_schwerbehindert_jn,
      validate: (value, entity) => {
        if (!value) {
          if (entity.p_schwerbehindert_jn) {
            return "Kein Grad der Behinderung angegeben";
          }
        }

        if (value && (value > 100 || value <= 0 || isNaN(value))) {
          return "Der Grad der Behinderung muss eine Zahl kleiner oder gleich 100 Prozent sein.";
        }
      },
      hideFromDetail: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      helpText: value => value >= 50
        ? "Wir bitten Sie, eine Kopie Ihres Schwerbehindertenausweises im Abschnitt \"Anlagen\" hochzuladen."
        : "Wir bitten Sie, eine Kopie Ihres Feststellungsbescheids und - wenn vorhanden - Ihres Gleichstellungsbescheid im Abschnitt \"Anlagen\" hochzuladen.",
      subtag: "stamm",
    },
    {
      field: "p_bescheidvon",
      display: entity => entity?.p_behindertengrad >= 50 ? "SB-Ausweis gültig ab" : "Feststellungsbescheid vom",
      type: "date",
      required: entity => !!entity.p_schwerbehindert_jn,
      validate: (value, entity) => {
        if (!value) {
          if (entity.p_schwerbehindert_jn) {
            return "Kein Datum angegeben";
          }
        }
      },
      hideFromDetail: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      hideFromEditor: orChecks(checkWerkvertragOderLehrvertrag, checkVortrag),
      subtag: "stamm",
    },
    // )-- Kein Lehrvertrag

    /*
     *{ PBP-1720: Hilfetext entfernen
     *  field: 'info_dokumente',
     *  type: 'plaintext',
     *  display: "Bitte fügen Sie Ihrem elektronischen Antrag eine Kopie der Geburtsurkunde oder Ihrer Heiratsurkunde bei. Später beim Einreichen des Vertrages werden diese Unterlagen als beglaubigte Kopie in Papierform benötigt."
     *          + "<br><br> Hinweis: Der Identitätsnachweis (Kopie der Geburts- oder Heiratsurkunde) wird benötigt, um Ihre Eingaben zu verifizieren. Dazu werden die folgenden Daten genutzt: Vorname, Nachname, Geburtsdatum, Geburtsort. Alle übrigen persönlichen Daten z.B. zu Ihren Eltern oder zu Ihrer Ehepartnerin/Ihrem Ehepartner sollten Sie schwärzen.",
     *  subtag: 'stamm',
     *  useHeader: false,
     *},
     */
    {
      field: "p_str",
      display: "Straße",
      maxlength: 50,
      subtag: "adresse",
      required: true,
    },
    {
      field: "p_plz",
      display: "Postleitzahl",
      validate: (value, entity) => {
        if (entity.p_land_ksaid !== KSA_DEUTSCHLAND) {
          return;
        }
        if (value && (isNaN(value) || parseInt(value) < 0 || String(value).length != 5)) {
          return "Die Postleitzahl muss eine 5-stellige Zahl sein";
        }
      },
      maxlength: 5,
      subtag: "adresse",
      required: entity => entity.p_land_ksaid === KSA_DEUTSCHLAND,
    },
    {
      field: "p_ort",
      display: "Ort",
      maxlength: 35,
      subtag: "adresse",
      required: true,
    },
    {
      field: "p_land_ksaid",
      display: "Land",
      initValue: KSA_DEUTSCHLAND,
      lookup: "k_staat.select.ksa_id.ksa_kbez",
      subtag: "adresse",
    },
    {
      field: "p_email",
      display: "E-Mail",
      maxlength: 100,
      subtag: "adresse",
      required: true,
    },
    {
      field: "p_tel",
      display: "Telefonnummer",
      helpText: "Rufnummer für eventuelle Rückfragen",
      maxlength: 50,
      subtag: "adresse",
      hideFromDetail: checkVortrag,
      hideFromEditor: checkVortrag,
    },
    {
      field: "p_ohne_verguetung",
      display: "ohne Vergütung",
      type: "checkbox",
      hideFromDetail: checkKeinLehrvertrag,
      hideFromEditor: checkKeinLehrvertrag,
      subtag: "bank",
      dependends: [
        {
          fields: ["p_bank", "p_iban", "p_bic", "p_kffid"],
          invisibleIf: value => value,
          noContainer: true,
        },
      ],
    },
    {
      field: "p_kontoinhaber",
      display: "Kontoinhaber*in",
      maxlength: 60,
      required: true,
      subtag: "bank",
      hideFromDetail: checkKeinVortrag,
      hideFromEditor: checkKeinVortrag,
    },
    {
      field: "p_bank",
      display: "Bankinstitut",
      maxlength: 60,
      required: true,
      subtag: "bank",
    },
    {
      field: "p_iban",
      display: "IBAN",
      maxlength: 40,
      required: true,
      subtag: "bank",
      selector: value => {
        let oIBAN = "";
        if (value) {
          for (let i = 0; i < value.length; i += 4) {
            oIBAN += value.substr(i, 4) + " ";
          }
          return oIBAN.replace(/\s$/, "");
        }
      },
      validate: value => {
        if (value && value.replace(" ", "").length > 35) {
          return "Bitte geben Sie eine gültige IBAN ein";
        }

        /*
         *if (value && /DE\d{2}[ ]\d{4}[ ]\d{4}[ ]\d{4}[ ]\d{4}[ ]\d{2}|DE\d{20}/.test(value.toUpperCase())==false) {
         *  return 'Bitte geben Sie eine gültige IBAN ein'
         *}
         */
      },
    },
    {
      field: "p_bic",
      display: "BIC",
      maxlength: 11,
      required: true,
      subtag: "bank",
      validate: value => {
        if (value && /^[a-z]{6}[0-9a-z]{2}([0-9a-z]{3})?/.test(value.toLowerCase()) == false) {
          return "Bitte geben Sie eine gültige BIC ein";
        }
      },
    },
    {
      field: "p_steuerid",
      display: "Steuer-ID",
      maxlength: 11,
      required: entity => entity.p_land_ksaid === KSA_DEUTSCHLAND,
      subtag: "bank",
      hideFromDetail: checkKeinSteuerId,
      hideFromEditor: checkKeinSteuerId,
      validate: (value, entity) => {
        if (_.isEmpty(value)) {
          if (entity.p_land_ksaid !== KSA_DEUTSCHLAND || entity.p_ohne_verguetung) {
            return;
          }

          return "Keine Steuer-ID angegeben";
        }

        if (value && (value.length < 11 || isNaN(value))) {
          return "Die Steuer-ID muss eine 11-stellige Ganzzahl sein";
        }

        // Prfüziffer checken
        let summe = 0;
        let produkt = 10;

        for (let i = 0; i <= 9; i++) {
          summe = (parseInt(value[i]) + produkt) % 10;
          if (summe == 0) {
            summe = 10;
          }
          produkt = (summe * 2) % 11;
        }
        let ziffer = 11 - produkt;
        if (ziffer == 10) {
          ziffer = 0;
        }
        if (value[10] != ziffer) {
          return "Die Steuer-ID ist nicht gültig";
        }
      },
      helpText: "Die Steueridentifikationsnummer, kurz Steuer-ID, wird für die jährliche Meldung an die Finanzbehörden gemäß Mitteilungsverordnung (MV) benötigt. Die Steuer-ID ist eine 11-stellige Ganzzahl.",
    },
    // (-- Nur für Lehrvertrag
    {
      field: "p_kffid",
      display: "Zuständiges Finanzamt",
      type: "select",
      droprichtung: "top",
      noSort: true,
      inject: ["k_finanzamtLookup"],
      choices: lookup => lookup && _.map(lookup, l => ({ value: l.kff_id, label: l.kff_gebiet }))
        .sort((a, b) => a.value === FINANZAMT.ANDERES ? -1 : b.value === FINANZAMT.ANDERES ? 1 : a.label.localeCompare(b.label)),
      selector: lookup => id => id && lookup && lookup[id] && lookup[id].kff_gebiet,
      hideFromDetail: checkKeinLehrvertragUndKeinVortrag,
      hideFromEditor: checkKeinLehrvertragUndKeinVortrag,
      required: false,
      validate: (value, entity, modus) => {
        const requiredNow = modus === ASTMODUSKEY.Vortrag
          ? entity.p_land_ksaid === KSA_DEUTSCHLAND
          : true;
        if (requiredNow && !value) {
          return "Zuständiges Finanzamt ist ein Pflichtfeld";
        }
      },
      subtag: "bank",
      dependends: [
        {
          fields: ["p_kffanderes_gebiet", "p_kffanderes_ort", "p_kffanderes_str", "p_kffanderes_plz"],
          invisibleIf: value => !value,
          noContainer: true,
        },
      ],
    },
    {
      field: "p_kffanderes_gebiet",
      display: "Gebiet",
      default: entity => (entity && entity.p_kffid === FINANZAMT.ANDERES && entity.p_kffanderes && entity.p_kffanderes.gebiet)
        || false,
      readonly: entity => !entity || entity.p_kffid !== FINANZAMT.ANDERES,
      hideFromDetail: checkKeinLehrvertragUndKeinVortrag,
      hideFromEditor: checkKeinLehrvertragUndKeinVortrag,
      subtag: "bank",
      inject: ["k_finanzamtLookup"],
      selector: lookup => (value, entity) => {
        const kffid = entity && (entity.p_kffid || entity.ap_kffid);
        const kffanderes = entity && (entity.p_kffanderes || entity.ap_kffanderes);
        return (kffid === FINANZAMT.ANDERES && kffanderes)
? kffanderes.gebiet
          : (lookup && lookup[kffid] && lookup[kffid].kff_gebiet) || null;
      },
    },
    {
      field: "p_kffanderes_ort",
      display: "Ort",
      default: entity => (entity && entity.p_kffid === FINANZAMT.ANDERES && entity.p_kffanderes && entity.p_kffanderes.ort)
        || false,
      readonly: entity => !entity || entity.p_kffid !== FINANZAMT.ANDERES,
      subtag: "bank",
      hideFromDetail: checkKeinLehrvertragUndKeinVortrag,
      hideFromEditor: checkKeinLehrvertragUndKeinVortrag,
      inject: ["k_finanzamtLookup"],
      selector: lookup => (value, entity) => {
        const kffid = entity && (entity.p_kffid || entity.ap_kffid);
        const kffanderes = entity && (entity.p_kffanderes || entity.ap_kffanderes);
        return (kffid === FINANZAMT.ANDERES && kffanderes)
? kffanderes.ort
          : (lookup && lookup[kffid] && lookup[kffid].kff_ort) || null;
      },
    },
    {
      field: "p_kffanderes_str",
      display: "Strasse und Hausnr.",
      default: entity => (entity && entity.p_kffiid === FINANZAMT.ANDERES && entity.p_kffanderes && entity.p_kffanderes.str)
        || false,
      readonly: entity => !entity || entity.p_kffid !== FINANZAMT.ANDERES,
      subtag: "bank",
      hideFromDetail: checkKeinLehrvertragUndKeinVortrag,
      hideFromEditor: checkKeinLehrvertragUndKeinVortrag,
      inject: ["k_finanzamtLookup"],
      selector: lookup => (value, entity) => {
        const kffid = entity && (entity.p_kffid || entity.ap_kffid);
        const kffanderes = entity && (entity.p_kffanderes || entity.ap_kffanderes);
        return (kffid === FINANZAMT.ANDERES && kffanderes)
? kffanderes.str
          : (lookup && lookup[kffid] && lookup[kffid].kff_str) || null;
      },
    },
    {
      field: "p_kffanderes_plz",
      display: "Postleitzahl",
      default: entity => (entity && entity.p_kffid === FINANZAMT.ANDERES && entity.p_kffanderes && entity.p_kffanderes.plz)
        || false,
      readonly: entity => !entity || entity.p_kffid !== FINANZAMT.ANDERES,
      subtag: "bank",
      hideFromDetail: checkKeinLehrvertragUndKeinVortrag,
      hideFromEditor: checkKeinLehrvertragUndKeinVortrag,
      inject: ["k_finanzamtLookup"],
      selector: lookup => (value, entity) => {
        const kffid = entity && (entity.p_kffid || entity.ap_kffid);
        const kffanderes = entity && (entity.p_kffanderes || entity.ap_kffanderes);
        return (kffid === FINANZAMT.ANDERES && kffanderes)
? kffanderes.plz
          : (lookup && lookup[kffid] && lookup[kffid].kff_plz) || null;
      },
    },
    // )-- Nur für Lehrvertrag
    {
      field: "info_steuerid_ausland",
      type: "plaintext",
      display: "Hinweis für Personen mit Wohnsitz außerhalb Deutschlands:<br>"
        + "Ich bestätige, dass ich eine deutsche Steuer-Identifikationsnummer beantragen und spätestens mit der Abrechnung einreichen werde. "
        + "Ich stimme zu, dass das Honorar erst dann ausgezahlt wird, wenn die oben genannte Steuer-Identifikationsnummer eingereicht wurde.<br>"
        + "Für die Beantragung ist das folgende Formular auszufüllen und an das Finanzamt Wuppertal zu senden. "
        + "<a href=\"#\" target=\"_blank\" rel=\"noreferrer\">TODO: Link zum Formular ergänzen</a>",
      useHeader: false,
      subtag: "bank",
      hideFromDetail: checkKeinVortrag,
      hideFromEditor: checkKeinVortrag,
    },
    {
      field: "p_buwbis",
      display: "Beschäftigt bei BUW bis",
      type: "date",
      subtag: "buwbv",
    },
    {
      field: "p_beschaeftigt",
      display: "Bei BUW beschäftigt als",
      lookup: "k_beschaeftigt.multiselect.kae_id.kae_kbez",
      subtag: "buwbv",
    },
    {
      field: "p_oeffdienstvon",
      display: "Im öffentlichen Dienst beschäftigt von",
      type: "date",
      subtag: "buwbv",
    },
    {
      field: "p_oeffdienstbis",
      display: "bis",
      type: "date",
      subtag: "buwbv",
    },

    // ---- Separater Block mit Fragen für Werkvertragsangaben
    {
      field: "p_keinarbeitnehmer",
      display: "Im Zusammenhang mit dem Werkvertrag beschäftige ich keine Arbeitnehmer, deren Arbeitsentgelt aus diesem Beschäftigungsverhältnis regelmäßig im Monat 450 Euro übersteigt.",
      lookup: "boolean.radioheader2",
      required: true,
      subtag: "werkvertrag",
    },
    {
      field: "p_nichtnuruni",
      display: "Ich führe die Tätigkeit auf Dauer und im Wesentlichen nicht nur für die Bergische Universität durch.",
      lookup: "boolean.radioheader2",
      required: true,
      subtag: "werkvertrag",
    },
    {
      field: "p_unternehmertaetigkeit",
      display: "Meine Tätigkeit lässt Merkmale unternehmerischen Handelns erkennen.",
      lookup: "boolean.radioheader2",
      required: true,
      subtag: "werkvertrag",
    },
    {
      field: "p_wiebeschaeftigunguni",
      display: "Meine Tätigkeit entspricht dem äußeren Erscheinungsbild nach der Tätigkeit, die ich zuvor für die Bergische Universität aufgrund eines Beschäftigungsverhältnisses ausgeübt habe.",
      lookup: "boolean.radioheader2",
      required: true,
      subtag: "werkvertrag",
    },

  ],
};

export function beforeSaveBank(entity, edits) {
  if (entity.p_kffid === FINANZAMT.ANDERES) {
    // initiales json schema festlegen und mit Werten aus den Schema Feldern belegen
    edits.p_kffanderes = entity.p_kffanderes ? entity.p_kffanderes : { gebiet: null, ort: null, str: null, plz: null };
    edits.p_kffanderes.gebiet = edits.p_kffanderes_gebiet !== undefined
      ? edits.p_kffanderes_gebiet
      : edits.p_kffanderes.gebiet;
    edits.p_kffanderes.ort = edits.p_kffanderes_ort !== undefined
      ? edits.p_kffanderes_ort
      : edits.p_kffanderes.ort;
    edits.p_kffanderes.str = edits.p_kffanderes_str !== undefined
      ? edits.p_kffanderes_str
      : edits.p_kffanderes.str;
    edits.p_kffanderes.plz = edits.p_kffanderes_plz !== undefined
      ? edits.p_kffanderes_plz
      : edits.p_kffanderes.plz;
  } else {
    edits.p_kffanderes = null;
  }
}

schema.table = setupLookups(schema);

// Schemas für Sub-Plugins
export const schema_stamm = buildSubSchema(schema, "stamm");
export const schema_adresse = buildSubSchema(schema, "adresse");
export const schema_bank = buildSubSchema(schema, "bank");
export const schema_buwbv = buildSubSchema(schema, "buwbv");
export const schema_werkvertrag = buildSubSchema(schema, "werkvertrag");

export const personRenderMethods = {
  p_beschaeftigt: {
    inject: "k_beschaeftigtLookup",
    generator: lookup => arr => {
      return _.map(arr, id => {
        return <div style={{ paddingBottom: "10px" }} key={id}>{lookup && lookup[id].kae_bez}</div>;
      });
    },
  },
};

const desc = {
  id: "p_id",
  name: "person",
  endpoint: "/api/shkperson/person",
  schema,
  breadcrumbs: {
    list: "Persönliche Daten",
    detail: ba => ba.ba_kbez,
  },
};

const personApi = api(desc);
export default personApi;
